import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { RETRO_VOTES_ENABLED } from "./retroConfig";

// Modal de alta/config del tablero (spec 006, RF-3; Incremento 2 RF-24). Selector de Ciclo/Sprint =
// IdProyecto poblado con la prop `proyectos` del portafolio (PA-2 resuelta, patrón de Retrospectivas
// 005/Aprendizajes — sin endpoint nuevo). En edición, IdProyecto es inmutable (el SP_RETRO_TABLERO_EDITAR
// no lo declara como parámetro, mismo patrón server-side que Aprendizajes/Retrospectivas 005): el select
// se deshabilita. El campo "Votos por usuario" se oculta con RETRO_VOTES_ENABLED=false (D8-3/RF-29; sigue
// enviándose con su default al backend, que lo tolera) y su lugar lo ocupa el nuevo campo de duración del
// timer (RF-24). La duración es SIEMPRE editable, incluso con la ceremonia en curso: editar el tiempo
// re-calcula el fin (TimerInicioUtc + duración) y permite alargar/acortar la retro sobre la marcha.
// Props tipables (listo para TS, §6.10).
//
// Props:
//  - show, onHide, saving: ver RetroCardModal
//  - proyectos: [{ IdProyecto, Nombre }]
//  - initialData: { Titulo, IdProyecto, MaxVotosPorUsuario, TimerDuracionMinutos, TimerInicioUtc } | null (null = alta)
//  - onSave: (data: { Titulo, IdProyecto, MaxVotosPorUsuario, TimerDuracionMinutos }) => void
//  - t: i18next (namespace "retroBoard")
const DEFAULT_MAX_VOTOS = 3;
const DEFAULT_TIMER_MINUTOS = 10;

const emptyForm = {
  Titulo: "",
  IdProyecto: "",
  MaxVotosPorUsuario: DEFAULT_MAX_VOTOS,
  TimerDuracionMinutos: DEFAULT_TIMER_MINUTOS,
};

const RetroBoardModal = ({ show, onHide, proyectos = [], initialData = null, saving = false, onSave, t }) => {
  const [formData, setFormData] = useState(emptyForm);
  const editing = !!initialData;

  useEffect(() => {
    if (show) {
      setFormData(
        initialData
          ? {
              Titulo: initialData.Titulo || "",
              IdProyecto: initialData.IdProyecto || "",
              MaxVotosPorUsuario:
                initialData.MaxVotosPorUsuario === undefined || initialData.MaxVotosPorUsuario === null
                  ? DEFAULT_MAX_VOTOS
                  : initialData.MaxVotosPorUsuario,
              TimerDuracionMinutos:
                initialData.TimerDuracionMinutos === undefined || initialData.TimerDuracionMinutos === null
                  ? DEFAULT_TIMER_MINUTOS
                  : initialData.TimerDuracionMinutos,
            }
          : emptyForm
      );
    }
  }, [show, initialData]);

  const handleChange = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  // El presupuesto de votos solo se valida si el sistema de votos está activo (D8-3/RF-29): con el flag
  // apagado el campo está oculto y su valor es dead code, así que no debe poder invalidar el formulario.
  const votosValid =
    !RETRO_VOTES_ENABLED ||
    (Number.isInteger(Number(formData.MaxVotosPorUsuario)) && Number(formData.MaxVotosPorUsuario) >= 0);
  const isValid =
    (formData.Titulo || "").trim() &&
    (editing || formData.IdProyecto) &&
    votosValid &&
    Number.isInteger(Number(formData.TimerDuracionMinutos)) &&
    Number(formData.TimerDuracionMinutos) >= 1;

  const handleSave = () => {
    if (!isValid) return;
    onSave({
      Titulo: formData.Titulo.trim(),
      IdProyecto: formData.IdProyecto || null,
      MaxVotosPorUsuario: Number(formData.MaxVotosPorUsuario),
      TimerDuracionMinutos: Number(formData.TimerDuracionMinutos),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="border-0 shadow-lg">
      <Modal.Header>
        <Modal.Title className="h5">
          {editing ? t("retro_modal_edit_title") : t("retro_modal_create_title")}
        </Modal.Title>
        <Button variant="light" onClick={onHide} aria-label="Close">
          X
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={12} className="mb-3 d-flex flex-column">
              <Form.Label>
                {t("retro_label_titulo")}
                <span className="text-primary"> *</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.Titulo}
                onChange={(e) => handleChange("Titulo", e.target.value)}
                autoFocus
              />
            </Col>
          </Row>
          <Row>
            <Col md={7} className="mb-3 d-flex flex-column">
              <Form.Label>
                {t("retro_label_sprint")}
                <span className="text-primary"> *</span>
              </Form.Label>
              <Form.Control
                as="select"
                disabled={editing}
                value={formData.IdProyecto}
                onChange={(e) => handleChange("IdProyecto", e.target.value)}
              >
                <option value="">{t("retro_sprint_select")}</option>
                {proyectos.map((p) => (
                  <option key={p.IdProyecto} value={p.IdProyecto}>
                    {p.Nombre}
                  </option>
                ))}
              </Form.Control>
              {editing && <Form.Text className="text-muted">{t("retro_sprint_immutable")}</Form.Text>}
            </Col>
            <Col md={5} className="mb-3 d-flex flex-column">
              {/* RF-24: duración del timer que gobierna la ceremonia. Ocupa el hueco visual que deja el campo
                  de votos oculto (D8-3). Siempre editable (también en curso): re-calcula el fin del timer. */}
              <Form.Label>
                {t("retro_label_timer_minutos")}
                <span className="text-primary"> *</span>
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={formData.TimerDuracionMinutos}
                onChange={(e) => handleChange("TimerDuracionMinutos", e.target.value)}
              />
            </Col>
          </Row>
          {RETRO_VOTES_ENABLED && (
            <Row>
              <Col md={5} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("retro_label_maxvotos")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={formData.MaxVotosPorUsuario}
                  onChange={(e) => handleChange("MaxVotosPorUsuario", e.target.value)}
                />
              </Col>
            </Row>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="dark" onClick={onHide} disabled={saving}>
          <i className="fa fa-times mr-2" />
          {t("retro_btn_cancel")}
        </Button>
        <Button variant="success" onClick={handleSave} disabled={saving || !isValid}>
          {saving ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <i className="fa fa-save mr-2" />
              {t("retro_btn_save")}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RetroBoardModal;
